import React, { useState, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeAnimatePresence from '../../components/ui/SafeAnimatePresence';
import {
  FiHash, FiX, FiMessageSquare, FiTrendingUp,
  FiRefreshCw, FiStar, FiBarChart2, FiCheck, FiPlus,
} from 'react-icons/fi';
import { useChannels } from '../../context/ChannelContext';
import type { Channel, Amp } from '../chat/chat.types';
import '../../styles/AddAmpToChannelModal.css';

interface AddAmpToChannelModalProps {
  isOpen: boolean;
  amp: Amp | null;
  onClose: () => void;
}

const CHANNEL_ICON_MAP: Record<string, React.ReactNode> = {
  hash: <FiHash size={16} />,
  message: <FiMessageSquare size={16} />,
  trending: <FiTrendingUp size={16} />,
  refresh: <FiRefreshCw size={16} />,
  star: <FiStar size={16} />,
  chart: <FiBarChart2 size={16} />,
};

const AddAmpToChannelModal: React.FC<AddAmpToChannelModalProps> = ({
  isOpen,
  amp,
  onClose,
}) => {
  const { channels, addAmpToChannel, removeAmpFromChannel, getChannelsForAmp } = useChannels();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const channelsWithAmp = useMemo(() => {
    if (!amp) return [];
    return getChannelsForAmp(amp.id);
  }, [amp, getChannelsForAmp]);

  const handleToggleChannel = useCallback((channel: Channel) => {
    if (!amp) return;

    const isInChannel = channelsWithAmp.some(c => c.guid === channel.guid);
    
    if (isInChannel) {
      removeAmpFromChannel(channel.guid, amp.id);
      setSuccessMessage(`Removed from ${channel.name}`);
    } else {
      addAmpToChannel(channel.guid, amp.id);
      setSuccessMessage(`Added to ${channel.name}`);
    }

    setTimeout(() => setSuccessMessage(null), 2000);
  }, [amp, channelsWithAmp, addAmpToChannel, removeAmpFromChannel]);

  if (!amp) return null;

  return (
    <SafeAnimatePresence>
      {isOpen && (
        <motion.div
          className="add-amp-channel__backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="add-amp-channel__modal glass-elevated"
            initial={{ opacity: 0, scale: 0.92, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 24 }}
            transition={{ duration: 0.3, ease: [0.34, 1.56, 0.64, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="add-amp-channel__header">
              <div className="add-amp-channel__amp-info">
                <div
                  className="add-amp-channel__amp-icon"
                  style={{ background: `${amp.color}15`, color: amp.color }}
                >
                  <FiStar size={18} />
                </div>
                <div>
                  <h2 className="add-amp-channel__title">{amp.name}</h2>
                  <p className="add-amp-channel__subtitle">Add to Workspace</p>
                </div>
              </div>
              <button className="add-amp-channel__close" onClick={onClose}>
                <FiX size={18} />
              </button>
            </div>

            {/* Success Message */}
            <SafeAnimatePresence>
              {successMessage && (
                <motion.div
                  className="add-amp-channel__success"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                >
                  <FiCheck size={14} />
                  {successMessage}
                </motion.div>
              )}
            </SafeAnimatePresence>

            <div className="add-amp-channel__content">
              {channels.length === 0 ? (
                <div className="add-amp-channel__empty">
                  <FiHash size={32} />
                  <p>No channels created yet</p>
                  <span>Create a channel first to add AMPs to it</span>
                </div>
              ) : (
                <div className="add-amp-channel__list">
                  {channels.map((channel) => {
                    const isInChannel = channelsWithAmp.some(c => c.guid === channel.guid);
                    return (
                      <motion.button
                        key={channel.guid}
                        className={`add-amp-channel__item ${isInChannel ? 'add-amp-channel__item--active' : ''}`}
                        onClick={() => handleToggleChannel(channel)}
                      >
                        <div
                          className="add-amp-channel__item-icon"
                          style={{ background: `${channel.color}15`, color: channel.color }}
                        >
                          {CHANNEL_ICON_MAP[channel.icon] || <FiHash size={16} />}
                        </div>
                        <div className="add-amp-channel__item-info">
                          <div className="add-amp-channel__item-name">{channel.name}</div>
                          <div className="add-amp-channel__item-desc">
                            {channel.ampIds.length} AMP{channel.ampIds.length !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className={`add-amp-channel__item-toggle ${isInChannel ? 'add-amp-channel__item-toggle--active' : ''}`}>
                          {isInChannel ? <FiCheck size={14} /> : <FiPlus size={14} />}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="add-amp-channel__footer">
              <div className="add-amp-channel__count">
                Added to {channelsWithAmp.length} channel{channelsWithAmp.length !== 1 ? 's' : ''}
              </div>
              <motion.button
                className="glass-btn glass-btn-primary add-amp-channel__done"
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Done
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </SafeAnimatePresence>
  );
};

export default AddAmpToChannelModal;
